import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { TokenChipInput } from '../../components/ui/TokenChipInput';
import { formatExpiry } from '../../lib/hostedReportsView';
import {
  buildEntryPatch,
  formFromEntry,
  portalAccessSentence,
  reachSentence,
  TILE_STATE_DISPLAY,
  type EntryEditForm,
} from '../../lib/portalLibrary/staffView';
import { listAccounts } from '../accounts';
import {
  PORTAL_LIBRARY_SECTION_LABELS,
  PORTAL_LIBRARY_SECTIONS,
  type PortalLibrarySection,
  type PortalLibraryStaffRow,
} from '../../types/portalLibrary';
import type { Account } from '../../types/account';
import { deletePortalEntry, listPortalLibrary, updatePortalEntry } from './api';
import { AudienceEditor, audienceBlocker, useAudienceReach } from './AudienceEditor';
import { CategoriesSection, CategorySelect, usePortalCategories } from './CategoryControls';
import { ThumbnailControl } from './ThumbnailControl';

// Tools → Portal library — the STAFF list of every tile on the client portal.
//
// ⚠️ A tile's state is shown in its OWN WORDS (TILE_STATE_DISPLAY): a revoked
// file, an ended tile and a deleted file need different fixes. Tiles are
// PUBLISHED from Tools → HTML hosting ("Add to Kyck portal" on the upload form);
// this page edits and removes them.

const SECTION_FILTER = [
  { value: '', label: 'All sections' },
  ...PORTAL_LIBRARY_SECTIONS.map((s) => ({ value: s, label: PORTAL_LIBRARY_SECTION_LABELS[s] })),
];

export function PortalLibraryPage() {
  const queryClient = useQueryClient();
  const [section, setSection] = useState('');
  const [editing, setEditing] = useState<PortalLibraryStaffRow | null>(null);
  const [deleting, setDeleting] = useState<PortalLibraryStaffRow | null>(null);

  const rowsQuery = useQuery({ queryKey: ['portal-library'], queryFn: listPortalLibrary });
  const categoriesQuery = usePortalCategories();
  const categoryName = (id: string | null) =>
    id ? (categoriesQuery.data ?? []).find((c) => c.id === id)?.name ?? 'Unknown category' : null;
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
    enabled: editing !== null,
  });

  const rows = useMemo(
    () => (rowsQuery.data ?? []).filter((r) => !section || r.entry.section === section),
    [rowsQuery.data, section],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['portal-library'] });
    void queryClient.invalidateQueries({ queryKey: ['hosted-reports'] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePortalEntry(id),
    onSuccess: () => {
      toast.success('Removed from the portal. The hosted report is untouched.');
      setDeleting(null);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Portal library"
        description="Strategy decks and Kyck college tiles on the client portal. Publish from HTML hosting with “Add to Kyck portal”."
      />

      <div className="w-60">
        <SelectField
          value={section}
          onValueChange={setSection}
          options={SECTION_FILTER}
          size="sm"
          aria-label="Section"
        />
      </div>

      {rowsQuery.isPending ? (
        <Spinner />
      ) : rowsQuery.isError ? (
        <p className="text-sm text-destructive">Couldn’t load the library.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">
          Nothing here yet. Upload a report in HTML hosting and tick “Add to Kyck portal”.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2">Tile</th>
                <th className="px-3 py-2">Section</th>
                <th className="px-3 py-2">Who sees it</th>
                <th className="px-3 py-2">Shown until</th>
                <th className="px-3 py-2">State</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const state = TILE_STATE_DISPLAY[row.tileState];
                return (
                  <tr key={row.entry.id} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{row.entry.title}</div>
                      <div className="text-xs text-slate-500">
                        {row.report ? `by ${row.report.createdByName}` : 'File no longer exists'}
                        {row.entry.period ? ` · ${row.entry.period}` : ''}
                        {row.entry.section === 'college'
                          ? ` · ${categoryName(row.entry.categoryId) ?? 'No category yet'}`
                          : ''}
                        {row.entry.tags.length > 0 ? ` · tags: ${row.entry.tags.join(', ')}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {PORTAL_LIBRARY_SECTION_LABELS[row.entry.section]}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      <div>{reachSentence(row.reach).replace('This will appear for ', '')}</div>
                      <div className="text-xs text-slate-700">{portalAccessSentence(row.reach)}</div>
                      <div className="text-xs text-slate-500">
                        {row.reach.perRule.map((r) => r.label).join(' · ')}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {formatExpiry(row.entry.visibleUntil)}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={state.variant} title={state.help}>
                        {state.label}
                      </Badge>
                      {row.tileState !== 'live' && (
                        <div className="mt-1 max-w-xs text-xs text-slate-500">{state.help}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {row.report && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={row.report.url} target="_blank" rel="noreferrer">
                              Open
                            </a>
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setEditing(row)}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(row)}>
                          Remove
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CategoriesSection />

      {editing && (
        <EditEntryDialog
          row={editing}
          accounts={accountsQuery.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}

      {deleting && (
        <Dialog open onOpenChange={(o) => !o && setDeleting(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove “{deleting.entry.title}” from the portal?</DialogTitle>
              <DialogDescription>
                Clients stop seeing the tile straight away. The hosted report itself is not
                deleted and keeps its current expiry — and anyone who saved the link can still
                open it until that expiry, or until you revoke it in HTML hosting.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleting.entry.id)}
              >
                Remove tile
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditEntryDialog({
  row,
  accounts,
  onClose,
  onSaved,
}: {
  row: PortalLibraryStaffRow;
  accounts: Account[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const entry = row.entry;
  const [form, setForm] = useState<EntryEditForm>(() => formFromEntry(entry));
  // The image saves on its own (upload is immediate), so it tracks its own copy
  // and refreshes the list at once — cancelling the dialog does not undo it.
  const queryClient = useQueryClient();
  const [thumbEntry, setThumbEntryState] = useState(entry);
  const setThumbEntry = (e: typeof entry) => {
    setThumbEntryState(e);
    void queryClient.invalidateQueries({ queryKey: ['portal-library'] });
  };
  const [error, setError] = useState<string | null>(null);
  const reach = useAudienceReach(form.audience, entry.section);
  const patch = buildEntryPatch(entry, form);
  const dirty = Object.keys(patch).length > 0;
  const blocker = patch.audience ? audienceBlocker(form.audience, reach) : null;

  const save = useMutation({
    mutationFn: () => updatePortalEntry(entry.id, patch),
    onSuccess: () => {
      toast.success('Saved.');
      onSaved();
    },
    onError: (err: Error) => setError(err.message),
  });

  const set = (p: Partial<EntryEditForm>) => setForm({ ...form, ...p });
  const section: PortalLibrarySection = entry.section;

  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit tile</DialogTitle>
          <DialogDescription>
            {PORTAL_LIBRARY_SECTION_LABELS[section]} · changes reach clients immediately. The end
            date is also the link’s expiry.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="ple-title">Title</Label>
            <Input id="ple-title" value={form.title} onChange={(e) => set({ title: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ple-summary">Summary</Label>
            <Input
              id="ple-summary"
              value={form.summary}
              maxLength={280}
              onChange={(e) => set({ summary: e.target.value })}
            />
          </div>
          {section === 'strategy.monthly' && (
            <div className="space-y-1.5">
              <Label htmlFor="ple-period">Month</Label>
              <Input
                id="ple-period"
                type="month"
                value={form.period}
                onChange={(e) => set({ period: e.target.value })}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Who sees it</Label>
            <AudienceEditor
              value={form.audience}
              onChange={(audience) => set({ audience })}
              accounts={accounts}
              section={section}
            />
          </div>
          {section === 'college' && (
            <div className="space-y-1.5">
              <Label htmlFor="ple-category">Category</Label>
              <CategorySelect
                id="ple-category"
                value={form.categoryId}
                onChange={(categoryId) => set({ categoryId })}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Tags</Label>
            <TokenChipInput value={form.tags} onChange={(tags) => set({ tags })} ariaLabel="Tags" />
            <p className="text-xs text-slate-500">Searchable by clients, never shown to them.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Tile image</Label>
            <ThumbnailControl entry={thumbEntry} onChanged={setThumbEntry} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ple-from">Show from</Label>
              <DatePicker
                id="ple-from"
                value={form.visibleFromDate}
                onChange={(iso) => set({ visibleFromDate: iso })}
                ariaLabel="Show from"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ple-until">Show until</Label>
              {!form.neverEnds && (
                <DatePicker
                  id="ple-until"
                  value={form.visibleUntilDate}
                  onChange={(iso) => set({ visibleUntilDate: iso })}
                  ariaLabel="Show until"
                />
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox
                  checked={form.neverEnds}
                  onCheckedChange={(v) => set({ neverEnds: v === true })}
                  aria-label="Never comes down"
                />
                Never comes down
              </label>
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {blocker && <p className="text-xs text-slate-500">{blocker}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button disabled={!dirty || save.isPending || blocker !== null} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
